import { t } from 'i18next';
import { Box, Tab, Typography } from '@mui/material';
import { TabContext, TabList, TabPanel } from '@mui/lab';

import { Dropdown } from '@components';
import { Link } from 'react-router-dom';
import { Folder, InsertDriveFile } from '@mui/icons-material';
import { ReportFoldersContext } from '@views/pages/Reporting/ReportFolders/context';
import { useReportFolders } from '@views/pages/Reporting/ReportFolders/hooks';
import {
  ReportFoldersPageTable,
  AddReportFolderModal,
} from '@views/pages/Reporting/ReportFolders/components';
import { formatCount } from '@util/number';

export const ReportFolders = () => {
  const {
    tabIndex,
    handleChangeTab,
    dropdownMenu,
    filters,
    handleFilterChange,
    isReportingSelected,
    isAddReportFolderModalOpen,
    handleCloseAddReportFolderModal,
    isInitialFilters,
    allCount,
    favoriteCount,
    createdByCount,
    recentCount,
  } = useReportFolders();

  const contextValues = {
    filters,
    handleFilterChange,
    isInitialFilters,
  };

  return (
    <ReportFoldersContext.Provider value={contextValues}>
      <Box
        sx={{
          height: '100%',
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant='h4' sx={{ mt: 2, mb: 2 }}>
            {t('reporting.folders.title')}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box component='nav' sx={{ display: 'flex' }}>
              <Link to='/reporting/reports'>
                <Box
                  sx={{
                    cursor: 'pointer',
                    height: '48px',
                    width: '120px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: 1,
                    border: theme => `1px solid ${theme.palette.grey[300]}`,
                    borderBottomLeftRadius: '4px',
                    borderTopLeftRadius: '4px',
                    background: theme =>
                      isReportingSelected
                        ? theme.palette.selectedPageBg.main
                        : 'unset',
                  }}
                >
                  <InsertDriveFile sx={{ height: '24px' }} color='primary' />
                  <Typography variant='body2'>
                    {t('reporting.nav.reports')}
                  </Typography>
                </Box>
              </Link>
              <Link to='/reporting/folders'>
                <Box
                  sx={{
                    cursor: 'pointer',
                    height: '48px',
                    width: '120px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: 1,
                    border: theme => `1px solid ${theme.palette.grey[300]}`,
                    borderBottomRightRadius: '4px',
                    borderTopRightRadius: '4px',
                    background: theme =>
                      isReportingSelected
                        ? 'unset'
                        : theme.palette.selectedPageBg.main,
                  }}
                >
                  <Folder sx={{ height: '24px' }} color='primary' />
                  <Typography variant='body2'>
                    {t('reporting.nav.folders')}
                  </Typography>
                </Box>
              </Link>
            </Box>
            <Dropdown menuList={dropdownMenu} />
          </Box>
        </Box>
        <TabContext value={tabIndex}>
          <Box>
            <Box
              sx={{
                borderBottom: 1,
                borderColor: 'divider',
                mb: 2,
                width: '100%',
              }}
            >
              <TabList onChange={handleChangeTab}>
                <Tab
                  label={`${t('reporting.folders.tab.all')} ${allCount > 0 ? '(' + formatCount(allCount) + ')' : ''}`}
                  value='1'
                />
                <Tab
                  label={`${t('reporting.folders.tab.favorite')} ${favoriteCount > 0 ? '(' + formatCount(favoriteCount) + ')' : ''}`}
                  value='2'
                />
                <Tab
                  label={`${t('reporting.folders.tab.recent')} ${recentCount > 0 ? '(' + formatCount(recentCount) + ')' : ''}`}
                  value='3'
                />
                <Tab
                  label={`${t('reporting.folders.tab.createdByMe')} ${createdByCount > 0 ? '(' + formatCount(createdByCount) + ')' : ''}`}
                  value='4'
                />
              </TabList>
            </Box>
            <>
              <TabPanel
                sx={{
                  padding: '0 !important',
                  width: '100%',
                }}
                value='1'
              >
                <ReportFoldersPageTable query='all' />
              </TabPanel>
              <TabPanel
                sx={{
                  padding: '0 !important',
                  width: '100%',
                }}
                value='2'
              >
                <ReportFoldersPageTable query='favorites_only' />
              </TabPanel>
              <TabPanel
                sx={{
                  padding: '0 !important',
                  width: '100%',
                }}
                value='3'
              >
                <ReportFoldersPageTable query='recently_viewed' />
              </TabPanel>
              <TabPanel
                sx={{
                  padding: '0 !important',
                  width: '100%',
                }}
                value='4'
              >
                <ReportFoldersPageTable query='created_by_me' />
              </TabPanel>
            </>
          </Box>
          <AddReportFolderModal
            isOpen={isAddReportFolderModalOpen}
            handleClose={handleCloseAddReportFolderModal}
          />
        </TabContext>
      </Box>
    </ReportFoldersContext.Provider>
  );
};
